import logo from "assets/images/integrations/mailchimp_logo.jpg";
import { Map } from "immutable";
import { useCallback, useMemo } from "react";
import { connect, ConnectedProps } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { RootReduxState } from "../../../../../utils/types";
import { ConnectedIntegrationLineWrapper } from "../../components/ConnectedIntegrationLineWrapper";
import { deleteIntegrationAction, saveIntegrationAction } from "../../redux/shopData/actions";
import { IntegrationType } from "../../types";
import { MailchimpSettingsForm } from "./SettingsForm";
import { MailchimpIntegrationSettings, MailchimpListsSettings } from "./types";

const mapStateToProps = (state: RootReduxState) => {
  const selectedShop = state.getIn([
    "customerBackendDashboard",
    "userData",
    "selectedShop"
  ]) as string;

  const integrationList = state
    .getIn([
      "customerBackendDashboard",
      "shops",
      selectedShop,
      "integrations",
      "data",
      IntegrationType.MAILCHIMP
    ])
    ?.toJS() as MailchimpIntegrationSettings[] | undefined;

  const connectedIntegration = integrationList?.find((integration) => {
    return integration.oath2Config && integration.oath2Config.accessToken;
  });

  const isDeleting = connectedIntegration
    ? (state.getIn([
        "customerBackendDashboard",
        "shops",
        selectedShop,
        "integrations",
        "UIStates",
        "deletingItem",
        IntegrationType.MAILCHIMP,
        connectedIntegration.id
      ]) as boolean) || false
    : false;

  const isSaving = connectedIntegration
    ? (state.getIn([
        "customerBackendDashboard",
        "shops",
        selectedShop,
        "integrations",
        "UIStates",
        "savingItem",
        IntegrationType.MAILCHIMP,
        connectedIntegration.id
      ]) as boolean) || false
    : false;

  return {
    selectedShop,
    connectedIntegration,
    isDeleting,
    isSaving
  };
};

const matchDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      deleteIntegrationAction,
      saveIntegrationAction
    },
    dispatch
  );
const connector = connect(mapStateToProps, matchDispatchToProps);

type MailchimpSettingsViewComponentProps = ConnectedProps<typeof connector>;

const MailchimpSettingsViewComponent = (props: MailchimpSettingsViewComponentProps) => {
  const {
    connectedIntegration,
    isDeleting,
    selectedShop,
    deleteIntegrationAction,
    isSaving,
    saveIntegrationAction
  } = props;

  const handleDelete = useCallback(() => {
    if (connectedIntegration) {
      deleteIntegrationAction(selectedShop, IntegrationType.MAILCHIMP, connectedIntegration.id);
    }
  }, [connectedIntegration, selectedShop]);

  const handleSave = useCallback(
    (values: MailchimpListsSettings) => {
      const duplicateApp = { ...connectedIntegration };
      Object.assign(duplicateApp, { settings: values });

      saveIntegrationAction(selectedShop, IntegrationType.MAILCHIMP, duplicateApp);
    },
    [connectedIntegration, selectedShop]
  );

  const listOptions = useMemo(
    () => connectedIntegration?.info?.lists || [],
    [connectedIntegration?.info?.lists]
  );

  const initialValues = useMemo(
    () => Map(connectedIntegration?.settings || {}),
    [connectedIntegration?.settings]
  );

  if (!connectedIntegration) {
    return null;
  }

  return (
    <ConnectedIntegrationLineWrapper
      accountName={connectedIntegration.info?.accountName}
      deleting={isDeleting}
      image={logo}
      name="MailChimp"
      onDelete={handleDelete}
    >
      <MailchimpSettingsForm
        listOptions={listOptions}
        initialValues={initialValues}
        saving={isSaving}
        handleSave={handleSave}
      />
    </ConnectedIntegrationLineWrapper>
  );
};

export const MailchimpSettingsView = connector(MailchimpSettingsViewComponent);
